import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import { Paper, Typography } from '@mui/material'
import { useState } from 'react'

import {
  OrganizationSetupModal,
  type ResumeSetupTarget,
} from '@/app/organizations/components/OrganizationSetupModal'
import { Box } from '@/components/ui'
import type { OrganizationDetail } from '@/definitions/organization'
import { useLocations } from '@/hooks/useLocations'
import { useStaff } from '@/hooks/useStaff'
import { canEnterSetup, getMissingSetupRecords } from '@/utils/getMissingSetupRecords'

interface ResumeSetupCardProps {
  organization: OrganizationDetail
}

interface ActionCardProps {
  icon: React.ReactNode
  title: string
  subtitle: string
  onClick: () => void
}

const ActionCard = ({ icon, title, subtitle, onClick }: ActionCardProps) => (
  <Paper
    variant='orgCard'
    onClick={onClick}
    tabIndex={0}
    role='button'
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        onClick()
      }
    }}
  >
    <Box variant='flexBetween'>
      <Box variant='flex' gap='md'>
        {icon}
        <Box variant='flexColumn' gap='xs' fullWidth={false}>
          <Typography variant='subtitle2'>{title}</Typography>
          <Typography variant='body2' color='text.secondary'>
            {subtitle}
          </Typography>
        </Box>
      </Box>
      <ChevronRightIcon fontSize='small' />
    </Box>
  </Paper>
)

/**
 * The only way back into a setup that stopped before it finished, since neither the
 * location nor the first admin is creatable anywhere else in this tool.
 */
export const ResumeSetupCard = ({ organization }: ResumeSetupCardProps) => {
  // Frozen at open: committing a record invalidates the reads below, which would
  // otherwise move the flow's starting point mid-run.
  const [resumeTarget, setResumeTarget] = useState<ResumeSetupTarget | null>(null)

  const orgId = organization.id
  const locations = useLocations(orgId)
  const staff = useStaff(orgId)

  const reads = {
    locations: {
      pending: locations.isLoading,
      failed: locations.isError,
      count: locations.data?.length ?? 0,
    },
    staff: {
      pending: staff.isLoading,
      failed: staff.isError,
      count: staff.data?.length ?? 0,
    },
  }
  const missing = getMissingSetupRecords(reads)

  // A location created outside this tool leaves `primary_location_id` unset. The
  // list is ordered oldest-first, so the fallback is the organization's first location.
  const existingLocation =
    locations.data?.find((location) => location.id === organization.primary_location_id) ??
    locations.data?.[0] ??
    null

  return (
    <>
      {canEnterSetup(reads) && (
        <Box variant='flexColumn' gap='xs'>
          <Typography variant='overline' color='text.secondary'>
            Setup Incomplete
          </Typography>
          <ActionCard
            icon={<PlaylistAddCheckOutlinedIcon color='action' />}
            title='Resume Setup'
            subtitle={`Still missing its ${missing.join(' and ')}`}
            onClick={() =>
              setResumeTarget({
                organization,
                existingLocation,
                // Entry waits for the staff read to settle, so this is a count and
                // not a guess — bar a failed read, which the create endpoint's
                // conflict is left to catch.
                hasStaff: reads.staff.count > 0,
              })
            }
          />
        </Box>
      )}
      {/* Outlives the offer on purpose: the last record commits before the flow
          reaches its review, which is where the offer stops being shown. */}
      {resumeTarget !== null && (
        <OrganizationSetupModal
          onClose={() => setResumeTarget(null)}
          resume={resumeTarget}
        />
      )}
    </>
  )
}
